import React, { useState, useEffect, useRef } from "react";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { TestSetService } from "../../../services/TestSetService";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import { Card, CardContent } from "../../ui/card";
import { Label } from "../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Slider } from "../../ui/slider";
import TestSetEligibleRequestsTable from "../TestSetEligibleRequestsTable";

interface TestSetData {
  requests?: ChatCompletionRequest[];
  sourceType?: "manual" | "intent_group" | "dataset";
  sampleType?: "all" | "random";
  samplePercentage?: number;
  datasetId?: number;
}

interface TestSetRequestsProps {
  intentGroupId: number;
  testSetData: TestSetData;
  onChange: (changes: Partial<TestSetData>) => void;
}

const TestSetRequests: React.FC<TestSetRequestsProps> = ({
  intentGroupId,
  testSetData,
  onChange,
}) => {
  const { apiKey } = usePermissions();

  const selectedRequests = testSetData.requests || [];
  const sourceType = testSetData.sourceType || "manual";
  const samplePercentage = testSetData.samplePercentage ?? 100;
  const datasetId = testSetData.datasetId;

  const sourceRadioValue = sourceType === "manual" ? "manual" : "sample";
  const sampleMethodValue =
    sourceType === "dataset" ? "dataset" : "intent_group";

  // State for eligible requests data fetching
  const [eligibleRequests, setEligibleRequests] = useState<ChatCompletionRequest[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalEligible, setTotalEligible] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialLoadDate] = useState<number>(Date.now());
  const eligibleRequestsCacheRef = useRef<{ [page: number]: ChatCompletionRequest[]; }>({});

  // State for datasets (when building from an existing dataset)
  const [datasets, setDatasets] = useState<FinetuneDataset[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState<boolean>(false);

  // Reset pagination/cache on intent group change
  useEffect(() => {
    eligibleRequestsCacheRef.current = {};
    setEligibleRequests([]);
    setTotalEligible(0);
    setCurrentPage(1);
  }, [intentGroupId]);

  // Fetch eligible requests
  useEffect(() => {
    if (sourceType !== "manual") return;
    if (intentGroupId) {
      fetchEligibleRequests(currentPage);
    }
  }, [currentPage, intentGroupId, pageSize, sourceType]);

  const fetchEligibleRequests = (page: number) => {
    if (eligibleRequestsCacheRef.current[page]) {
      setEligibleRequests(eligibleRequestsCacheRef.current[page]);
      return;
    }

    const offset = (page - 1) * pageSize;
    setIsLoading(true);

    // For new test sets, use -1 as testSetId
    TestSetService.getEligibleTestSetRequests(
      intentGroupId,
      -1, // No existing test set
      initialLoadDate,
      offset,
      pageSize,
      null,
      setIsLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        const items = data.items || [];
        const totalCount = data.total || 0;

        setEligibleRequests(items);
        setTotalEligible(totalCount);
        eligibleRequestsCacheRef.current = {
          ...eligibleRequestsCacheRef.current,
          [page]: items,
        };
        setIsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
      }
    );
  };

  const handleRequestUpdate = (updatedRequest: ChatCompletionRequest) => {
    // Update the request in the selected requests if it exists
    const updatedRequests = selectedRequests.map(request =>
      request.request_id === updatedRequest.request_id ? updatedRequest : request
    );
    
    // If the request wasn't in selected requests, add it
    if (!selectedRequests.some(r => r.request_id === updatedRequest.request_id)) {
      updatedRequests.push(updatedRequest);
    }
    
    onChange({ requests: updatedRequests });

    // Also update the eligible requests if it exists there
    setEligibleRequests(prev =>
      prev.map(request =>
        request.request_id === updatedRequest.request_id ? updatedRequest : request
      )
    );

    // Update cache
    if (eligibleRequestsCacheRef.current[currentPage]) {
      eligibleRequestsCacheRef.current[currentPage] =
        eligibleRequestsCacheRef.current[currentPage].map(request =>
          request.request_id === updatedRequest.request_id ? updatedRequest : request
        );
    }
  };

  const totalPages = Math.ceil(totalEligible / pageSize);

  // Fetch datasets when creating from dataset source
  useEffect(() => {
    if (sourceType !== "dataset") return;
    if (!intentGroupId) return;

    const sub = DatasetService.getDatasetsByIntentGroup(
      intentGroupId,
      0,
      200,
      setDatasetsLoading,
      apiKey
    ).subscribe(
      (resp: any) => {
        setDatasets(resp?.items || resp || []);
      },
      (error: any) => {
        console.error("Error fetching datasets for test set:", error);
        setDatasets([]);
      }
    );

    return () => sub.unsubscribe();
  }, [sourceType, intentGroupId, apiKey]);

  const selectedDataset = datasetId
    ? datasets.find((d) => d.id === datasetId) || null
    : null;

  const handleSourceChange = (value: string) => {
    if (value === "manual") {
      onChange({ sourceType: "manual" });
      return;
    }

    // Sample (default to sampling across intent group)
    const nextPct =
      sourceType === "manual" ? 10 : (testSetData.samplePercentage ?? 10);
    onChange({
      sourceType: sourceType === "manual" ? "intent_group" : sourceType,
      samplePercentage: nextPct,
      sampleType: nextPct >= 100 ? "all" : "random",
      datasetId: sourceType === "manual" ? undefined : datasetId,
      requests: [],
    });
  };

  const handleSampleMethodChange = (value: string) => {
    const nextPct = testSetData.samplePercentage ?? 10;
    if (value === "dataset") {
      onChange({
        sourceType: "dataset",
        samplePercentage: nextPct,
        sampleType: nextPct >= 100 ? "all" : "random",
        requests: [],
      });
      return;
    }
    // intent_group
    onChange({
      sourceType: "intent_group",
      samplePercentage: nextPct,
      sampleType: nextPct >= 100 ? "all" : "random",
      datasetId: undefined,
      requests: [],
    });
  };

  const handleSamplePercentageChange = (values: number[]) => {
    const pct = values[0];
    onChange({
      samplePercentage: pct,
      sampleType: pct >= 100 ? "all" : "random",
    });
  };

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="space-y-2">
        <h3 className="text-xl font-gc-med text-foreground">Select Requests</h3>
        <p className="text-sm text-muted-foreground">
          Choose how this test set should be built
        </p>
      </div>

      {/* Source Selection */}
      <Card className="border-border">
        <CardContent className="p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-gc-med text-foreground">Source</Label>
            <p className="text-xs text-muted-foreground">
              Manually select specific requests, or sample requests across the intent group or within a dataset.
            </p>
          </div>

          <RadioGroup value={sourceRadioValue} onValueChange={handleSourceChange} className="space-y-4">
            <div className="flex items-start gap-3">
              <RadioGroupItem value="manual" id="ts-source-manual" className="mt-1" />
              <div className="space-y-1">
                <Label htmlFor="ts-source-manual" className="text-sm font-gc-med text-foreground">
                  Manual selection
                </Label>
                <p className="text-xs text-muted-foreground">
                  Select individual requests and optionally tag them.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <RadioGroupItem value="sample" id="ts-source-sample" className="mt-1" />
              <div className="space-y-1">
                <Label htmlFor="ts-source-sample" className="text-sm font-gc-med text-foreground">
                  Sample
                </Label>
                <p className="text-xs text-muted-foreground">
                  Choose where to sample from and what percentage to include.
                </p>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Sampling options */}
      {sourceType !== "manual" && (
        <Card className="border-border">
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">Sample method</Label>
              <Select
                value={sampleMethodValue}
                onValueChange={handleSampleMethodChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a sampling method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="intent_group">Sample across intent group</SelectItem>
                  <SelectItem value="dataset">Sample within a dataset</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {sourceType === "dataset" && (
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label className="text-sm font-gc-med text-foreground">Dataset</Label>
                  <Select
                    value={datasetId ? String(datasetId) : undefined}
                    onValueChange={(value) => onChange({ datasetId: Number(value) })}
                    disabled={datasetsLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={datasetsLoading ? "Loading datasets..." : "Select a dataset"} />
                    </SelectTrigger>
                    <SelectContent>
                      {datasets.map((d) => (
                        <SelectItem key={d.id} value={String(d.id)}>
                          {d.name} ({d.request_count?.toLocaleString?.() || d.request_count || 0} requests)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedDataset && (
                  <div className="text-xs text-muted-foreground">
                    Selected: <span className="text-foreground font-gc-med">{selectedDataset.name}</span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-gc-med text-foreground">Sample percentage</Label>
                <div className="text-sm font-gc-med text-foreground">{samplePercentage}%</div>
              </div>
              <Slider
                min={1}
                max={100}
                step={1}
                value={[samplePercentage]}
                onValueChange={handleSamplePercentageChange}
              />
              <p className="text-xs text-muted-foreground">
                We’ll randomly select approximately {samplePercentage}% of eligible requests.
              </p>
            </div>

            {sourceType === "dataset" && (
              <div className="pt-2">
                {samplePercentage >= 100 ? (
                  <p className="text-xs text-muted-foreground">
                    Sampling <span className="font-gc-med">100%</span> will mark this dataset as{" "}
                    <span className="font-gc-med">Used in Test Set</span> and it won’t be usable for compositions / finetunes.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Sampling <span className="font-gc-med">&lt; 100%</span> will keep this dataset usable for compositions / finetunes,
                    and it will show a <span className="font-gc-med">Contains test requests</span> label.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selection Summary */}
      {sourceType === "manual" && selectedRequests.length > 0 && (
        <Card className="border-border bg-maitai-green/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-gc-med text-foreground">
                {selectedRequests.length} request{selectedRequests.length !== 1 ? "s" : ""} selected
              </span>
              <span className="text-muted-foreground">for test set</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Requests Table */}
      {sourceType === "manual" && (
        <TestSetEligibleRequestsTable
          requests={eligibleRequests}
          loading={isLoading}
          apiKey={apiKey}
          selectedRequests={selectedRequests}
          onSelectionChange={(requests) => onChange({ requests })}
          onRequestUpdate={handleRequestUpdate}
          selectable={true}
          showOnlySelected={false}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalEligible}
          itemsPerPage={pageSize}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

export default TestSetRequests;
